import { PromptType } from "../ai/AIAssistant";
import { isOk } from "../common/common";
import { languageLabels, TranslationDirection } from "../i18n/languages";
import { TMessage } from "../i18n/tt";
import { observer } from "mobx-react";
import { useDispatch, useTranslation } from "../stores/RootStoreContext";
import { Box, VBox } from "../components-v2/Box";
import { AIAssistantError, AICustomQueryDialog, AIProgressBar } from "../ai/AIAssistantComponents";
import { LoadingIndicator } from "../components-v2/LoadingIndicator";
import { MTextInput } from "../components-v2/TextInput";
import { when } from "../utils/functions";
import { FC } from "react";
import { Checkbox } from "../components-v2/Checkbox";
import { IconButton } from "../components-v2/Button";
import { MarkdownEditor } from "./MarkdownEditor";
import { LocalizedTextEditorModel } from "./localizedTextEditorDomain";
import { AutotextDialog } from "../ai/autotextComponents";

export const AutoTranslateOptions: FC<{
  autoTranslate: boolean;
  translationDirection?: TranslationDirection;
  toggleTranslationDirection: () => void;
  toggleAutoTranslate: () => void;
}> = observer((props) => {
  const { t } = useTranslation();
  if (!props.translationDirection) {
    return null;
  }
  return (
    <Box justifyContent="space-between" alignItems="center">
      <Checkbox
        label={
          <Box alignItems="start" justifyContent={"start"}>
            <Box>
              {t("ai.translation.auto", {
                from: t(languageLabels[props.translationDirection.from]),
              })}
            </Box>
            <IconButton
              icon={"swap"}
              tooltip={t("swap-languages")}
              onClick={props.toggleTranslationDirection}
              compact
              disabled={!props.autoTranslate}
            />
            <span>{t("to").toLowerCase()}</span>
            {props.translationDirection && t(languageLabels[props.translationDirection.to])}
          </Box>
        }
        checked={props.autoTranslate}
        onChange={props.toggleAutoTranslate}
      />
    </Box>
  );
});

export const AutoTranslateOptionsV2: FC<{
  autoTranslate: boolean;
  translationDirection?: TranslationDirection;
  toggleTranslationDirection: () => void;
  toggleAutoTranslate: () => void;
}> = observer((props) => {
  const { t } = useTranslation();
  if (!props.translationDirection) {
    return null;
  }
  return (
    <Box>
      <Checkbox
        label={
          <Box alignItems="start" justifyContent={"start"}>
            <Box>{t("ai.auto")}</Box>
            <IconButton
              icon={"arrows-down-up"}
              tooltip={t("swap-languages")}
              onClick={props.toggleTranslationDirection}
              compact
              disabled={!props.autoTranslate}
            />
          </Box>
        }
        checked={props.autoTranslate}
        onChange={props.toggleAutoTranslate}
      />
    </Box>
  );
});

export const LocalizedTextEditor = observer(function LocalizedTextEditor({
  model,
  label,
  autofocus,
  useV2AutotranslateOption,
  hideTranslationOptions,
  inputType = "markdown",
  placeholder,
}: {
  model: LocalizedTextEditorModel;
  label?: TMessage;
  autofocus?: boolean;
  useV2AutotranslateOption?: boolean;
  hideTranslationOptions?: boolean;
  inputType?: "markdown" | "string";
  placeholder?: TMessage;
}) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  return (
    <VBox>
      {t(label ?? model.label)}
      {!useV2AutotranslateOption &&
        model.textModels.length > 1 &&
        model.translationDirection &&
        !hideTranslationOptions && (
          <AutoTranslateOptions
            autoTranslate={model.autoTranslate}
            translationDirection={model.translationDirection}
            toggleAutoTranslate={model.toggleAutoTranslate}
            toggleTranslationDirection={model.toggleTranslationDirection}
          />
        )}
      {model.textModels.map((localized) => {
        const aiAssistant = localized.aiAssistant!;
        const includeAssistantOptions =
          model.translationDirection?.from === localized.locale ||
          !model.autoTranslate ||
          model.textModels.length === 1;
        const languageLabel =
          model.textModels.length > 1 ? (
            useV2AutotranslateOption &&
            !hideTranslationOptions &&
            localized.locale !== model.translationDirection?.from ? (
              <Box justifyContent="space-between" alignItems="center">
                {t(languageLabels[localized.locale])}{" "}
                {!hideTranslationOptions && (
                  <AutoTranslateOptionsV2
                    autoTranslate={model.autoTranslate}
                    translationDirection={model.translationDirection}
                    toggleAutoTranslate={model.toggleAutoTranslate}
                    toggleTranslationDirection={model.toggleTranslationDirection}
                  />
                )}
              </Box>
            ) : (
              t(languageLabels[localized.locale])
            )
          ) : undefined;
        return (
          <VBox key={localized.locale}>
            {localized.aiAssistant?.autotextDialogProps && (
              <AutotextDialog {...localized.aiAssistant?.autotextDialogProps} />
            )}
            <AIAssistantError result={aiAssistant.translateMessageResult} />
            <AIAssistantError result={aiAssistant.improveMessageResult} />
            {localized.aiAssistant && (
              <AICustomQueryDialog
                aiAssistant={localized.aiAssistant}
                language={localized.language}
              />
            )}
            {aiAssistant.showTranslateProgressBar && (
              <LoadingIndicator label={t("translating")} size={"sm"} />
            )}
            {inputType === "markdown" ? (
              aiAssistant.showImproveMessageProgressBar ? (
                <AIProgressBar
                  message={aiAssistant.progress}
                  finished={isOk(aiAssistant.improveMessageResult)}
                  onCancel={aiAssistant.cancelImproveMessageResult}
                  onApply={() => model.applyImproveMessageResult(localized)}
                />
              ) : (
                <MarkdownEditor
                  editorRef={(api) => aiAssistant.setEditorApi(api)}
                  customOptions={
                    includeAssistantOptions
                      ? aiAssistant.getCustomOptions(
                          t,
                          localized.model.inputValue,
                          localized.language,
                          PromptType.FREETEXT_DESCRIPTION,
                        )
                      : undefined
                  }
                  label={languageLabel}
                  value={localized.model.inputValue}
                  onChange={localized.model.setAndCommitInputValue}
                  onInput={() => {
                    if (model.disableAutoTranslate(localized)) {
                      dispatch([
                        {
                          type: "show-alert",
                          message: { key: "ai.auto-translate-deactivated" },
                          messageType: "info",
                        },
                      ]);
                    }
                  }}
                  readOnly={model.isReadOnly(localized)}
                  autofocus={autofocus && model.textModels[0] === localized}
                  placeholder={when(placeholder, t)}
                />
              )
            ) : (
              <MTextInput
                label={languageLabel}
                model={localized.model}
                onChange={(event) => {
                  localized.model.setInputValue(event);
                  if (model.disableAutoTranslate(localized)) {
                    dispatch([
                      {
                        type: "show-alert",
                        message: { key: "ai.auto-translate-deactivated" },
                        messageType: "info",
                      },
                    ]);
                  }
                }}
                readOnly={model.isReadOnly(localized)}
                autofocus={autofocus && model.textModels[0] === localized}
                placeholder={when(placeholder, t)}
              />
            )}
          </VBox>
        );
      })}
    </VBox>
  );
});
